import React, { useEffect, useMemo, useRef, useState } from "react";

export type RecordingRoomItem = "wms" | "ms" | "extra" | "ar" | "am" | "lav";

interface RecordingRoomDiagramProps {
  onItemSelect: (item: RecordingRoomItem | null) => void;
  SvgComponent: React.ComponentType;
  diagramKey: string; // Add key to help React identify the component instance
}

export const RecordingRoomDiagram: React.FC<RecordingRoomDiagramProps> = ({
  onItemSelect,
  SvgComponent,
  diagramKey,
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState<boolean>(false);

  // Track when SVG content is ready
  const [svgReady, setSvgReady] = useState<boolean>(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Effect to detect when SVG content is available
  useEffect(() => {
    if (!mounted || !divRef.current) {
      setSvgReady(false);
      return;
    }

    // Use a timeout to ensure SVG is fully rendered
    const timeoutId = setTimeout(() => {
      setSvgReady(true);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [mounted, diagramKey]);

  // memoize item handles - recalculate when SVG is ready
  const itemHandles = useMemo<Partial<Record<RecordingRoomItem, Element>>>(() => {
    if (!svgReady || !divRef.current) {
      return {};
    }

    const handles = {
      wms: divRef.current.querySelector(".label-wms") ?? undefined,
      ms: divRef.current.querySelector(".label-ms") ?? undefined,
      extra: divRef.current.querySelector(".label-extra") ?? undefined,
      ar: divRef.current.querySelector(".label-ar") ?? undefined,
      am: divRef.current.querySelector(".label-am") ?? undefined,
      lav: divRef.current.querySelector(".label-lav") ?? undefined,
    };

    return handles;
  }, [svgReady]);

  // setup click listeners
  useEffect(() => {
    if (!svgReady || !divRef.current) return;

    const listeners: Record<string, (event: Event) => void> = {};

    // Add click listeners to individual labels
    Object.entries(itemHandles).forEach(([id, handle]) => {
      if (!handle) {
        // eslint-disable-next-line no-console
        return;
      }
      listeners[id] = (event: Event) => {
        // Prevent event from bubbling to the container
        event.stopPropagation();
        onItemSelect(id as RecordingRoomItem);
      };

      handle.addEventListener("click", listeners[id]);
    });

    // Add click listener to the container for click-outside detection
    const containerClickListener = (event: Event): void => {
      // Check if the click target is one of the label elements
      const target = event.target as Element;
      const isLabelClick = Object.values(itemHandles).some(
        (handle) => handle && (handle === target || handle.contains(target))
      );

      if (!isLabelClick) {
        onItemSelect(null);
      }
    };

    const containerElement = divRef.current;
    containerElement.addEventListener("click", containerClickListener);

    return () => {
      // Remove label listeners
      Object.entries(itemHandles).forEach(([id, handle]) => {
        if (listeners[id] && handle) {
          handle.removeEventListener("click", listeners[id]);
        }
      });

      // Remove container listener
      if (containerElement) {
        containerElement.removeEventListener("click", containerClickListener);
      }
    };
  }, [svgReady, itemHandles, onItemSelect, diagramKey]);

  return (
    <div ref={divRef}>
      <SvgComponent />
    </div>
  );
};
